import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Accordion, Button, Stack, Table, Text } from "@mantine/core";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { ErrorAlert, TimeAgo } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import z from "zod";

import { api } from "@/api.ts";
import Area from "@/components/Area.tsx";
import DownloadStatusIcon from "@/components/DownloadStatusIcon";
import { useCatalogItems } from "@/hooks/useCatalogItems.ts";
import useDownload from "@/hooks/useDownload.ts";
import { useFormatItems } from "@/hooks/useFormatItems.ts";

export const Route = createFileRoute("/flows/download/wait/$id")({
  component: RouteComponent,
  params: z.object({ id: z.coerce.number().int() }),
  head: () => ({
    meta: [
      {
        title:
          globalT`Sweco REALity` +
          " - " +
          globalT`Download` +
          " - " +
          globalT`Wait for download`,
      },
    ],
  }),
});

function RouteComponent() {
  const id = Route.useParams().id;
  const { data } = useQuery(
    api.from("download").select("status").eq("id", id).single(),
    {
      refetchInterval: (query) =>
        ["queued", "in_progress", undefined].includes(
          query.state.data?.data?.status,
        )
          ? 1000
          : false,
    },
  );
  // separate query for details to avoid refetching every second
  const { data: details } = useQuery(
    api
      .from("download")
      .select(
        "queued_by,queued_at,datasets,geometry,area,format,status,project(identifier,name)",
      )
      .eq("id", id)
      .single(),
  );
  const catalog = useCatalogItems();

  const formats = useFormatItems();
  const download = useDownload();
  const { t } = useLingui();

  if (!data || !details) {
    return (
      <span>
        <Trans>Loading...</Trans>
      </span>
    );
  }

  return (
    <Stack align="center" justify="center" gap="xl" my="xl">
      <DownloadStatusIcon status={data.status} size="4x" />
      <Text fz="xl">
        {
          {
            queued: t`Queued`,
            in_progress: t`In Progress`,
            completed: t`Completed`,
            failed: t`Failed`,
          }[data.status]
        }
      </Text>
      {data.status === "completed" && (
        <Button
          onClick={() => download.download({ id })}
          loading={!!download.loading}
          color="green"
        >
          <Trans>Download</Trans>
        </Button>
      )}
      {download.error && <ErrorAlert>{download.error}</ErrorAlert>}

      <Accordion w="50%" miw="400px">
        <Accordion.Item value="details" bd="none">
          <Accordion.Control>
            <Trans>Details</Trans>
          </Accordion.Control>
          <Accordion.Panel>
            <Table variant="vertical" layout="fixed">
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th bg="white">
                    <Trans>Project</Trans>
                  </Table.Th>
                  <Table.Td>
                    {details.project.name} ({details.project.identifier})
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th bg="white">
                    <Trans>Requested at</Trans>
                  </Table.Th>
                  <Table.Td>
                    <TimeAgo date={details.queued_at} />
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th bg="white">
                    <Trans>Format</Trans>
                  </Table.Th>
                  <Table.Td>{formats[details.format]?.title}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th bg="white">
                    <Trans>Datasets</Trans>
                  </Table.Th>
                  <Table.Td>
                    {details.datasets
                      .map((uri) => catalog.forUri(uri)?.properties.title)
                      .join(", ")}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th bg="white">
                    <Trans>Area</Trans>
                  </Table.Th>
                  <Table.Td>
                    <Area value={details.area} />
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
